import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  PlusOutlined,
  UserOutlined,
  EllipsisOutlined,
  RightSquareOutlined,
  LeftSquareOutlined,
  MessageOutlined,
  ProfileOutlined,
  DeleteOutlined,
  EditOutlined,
  MoonOutlined,
  SunOutlined,
  SettingOutlined,
  LogoutOutlined,
  AlignLeftOutlined,
  PlusSquareOutlined,
  AlignRightOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import {
  Layout,
  Menu,
  Image,
  Modal,
  Row,
  Col,
  Popover,
  Button,
  Dropdown,
  Spin,
  Tooltip,
  Typography,
  Space,
  Drawer,
  notification,
  Divider,
} from 'antd';
import avatarDay from '@/assets/logo.png';
import { updateNameAPI } from '@/services/report.js';
import { deleteConversationApi, updateConversationNameAPI } from '@/services/chat.js';
import './index.css';
import { config } from '@/config/index.js';
import { logout, updateUserProfile } from '@/services/user.js';
import { useSelector, useDispatch } from 'react-redux';
import { checkLoginStatus } from '@/store/modules/authSlice.js';
import { setCurrentReportId, setReportId, setChatId } from '@/store/modules/reportSlice.js';
import { setIsShowSystemTour } from '@/store/modules/chatSlice.js';
import {
  deleteConversion,
  getConversionListThunk,
  getRepostLitThunk,
  setCurrentTitle,
  setThemeMode,
  updateConversionAttr,
  updateReportAttr,
  updateUserProfileState,
} from '@/store/modules/globalSlice.js';
import Setting from './components/Setting.js';
import { useRequest } from 'ahooks';
import QuestionCenter from './components/QuestionCenter.js';
import CustomAvatar from '@/components/CustomAvatar/index.js';
import LoginModal from './components/LoginModal.js';
import RenameInput from './components/RenameInput.js';
import { useTranslation } from 'react-i18next';

const { Content, Sider, Header } = Layout;
const { Text } = Typography;
const { confirm } = Modal;

const HomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { externalRef } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { t } = useTranslation('home', 'translation');

  const [collapsed, setCollapsed] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [menuSelectedKeys, setMenuSelectedKeys] = useState([]);
  const [menuOpenKeys, setMenuOpenKeys] = useState([]);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isMobileSiderOpen, setIsMobileSiderOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [renameId, setRenameId] = useState('');
  const [isSettingModalOpen, setIsSettingModalOpen] = useState(false);
  const [activePopoverId, setActivePopoverId] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const currentExternalRef = useRef(externalRef);

  const [isMobile, setIsMobile] = useState(window.matchMedia('(max-width: 768px)').matches);

  const { userProfile, conversionList, reportList, themeMode, currentTitle } = useSelector(
    state => state.global
  );
  const { isLoggedIn } = useSelector(state => state.auth);

  const { runAsync: updateThemeApi } = useRequest(updateUserProfile, {
    manual: true,
  });

  const [hasShownNotification, setHasShownNotification] = useState(false);

  // 新增状态
  const [hiddenMessageId, setHiddenMessageId] = useState({});

  useEffect(() => {
    if (userProfile && userProfile.language && userProfile.language !== i18n.language) {
      i18n.changeLanguage(userProfile.language);
    }
    if (userProfile) {
      const { firstBoot } = userProfile;
      if (0 === firstBoot) {
        dispatch(setIsShowSystemTour(true));
        updateThemeApi({ firstBoot: 1 }).then(() => {
          dispatch(updateUserProfileState({ firstBoot: 1 }));
        });
      }
    }
  }, [userProfile, dispatch]);

  useEffect(() => {
    currentExternalRef.current = externalRef;
  }, [externalRef]);

  useEffect(() => {
    if (!hasShownNotification && conversionList.length > 0) {
      const flag = conversionList.filter(
        file => file.isWhitelistFlag == '1' || file.auditStatus == '1'
      );
      if (flag.length > 0 && userProfile.userStatus === 'Login User') {
        notification.success({
          message: t('chat:warning'),
        });
        setHasShownNotification(true);
      }
    }
  }, [conversionList, hasShownNotification]);
  //判断report是否已经进入了视口
  const [isReport, setIsReport] = useState(false);
  const reportDividerRef = useRef(null);
  const conversationDividerRef = useRef(null);
  const handleMenuScroll = () => {
    if (reportDividerRef.current && menuScrollRef.current) {
      const dividerRect = reportDividerRef.current.getBoundingClientRect();
      const menuRect = menuScrollRef.current.getBoundingClientRect();

      // 判断 reportDivider 是否在 menuScroll 容器内可见
      const isVisible = dividerRect.top < menuRect.bottom && dividerRect.bottom > menuRect.top;

      if (isVisible) {
        setIsReport(true);
      } else {
        setIsReport(false);
      }
    }
  };
  //判断report是否快要离开视口
  const [isReportLeaving, setIsReportLeaving] = useState(false);
  const checkReportDividerPosition = () => {
    if (reportDividerRef.current && menuScrollRef.current) {
      const dividerRect = reportDividerRef.current.getBoundingClientRect();
      const menuRect = menuScrollRef.current.getBoundingClientRect();
      const offset = dividerRect.top - menuRect.top;
      if (offset <= 50) {
        setIsReportLeaving(true);
      } else {
        setIsReportLeaving(false);
      }
    }
  };
  const onMenuScroll = () => {
    handleMenuScroll();
    checkReportDividerPosition();
  };
  const scrollToRef = (ref, offset = 0) => {
    if (ref.current && menuScrollRef.current) {
      const container = menuScrollRef.current;
      const containerRect = container.getBoundingClientRect();
      const targetRect = ref.current.getBoundingClientRect();

      const containerScrollTop = container.scrollTop;

      const targetTopRelativeToContainer = targetRect.top - containerRect.top;
      const targetOffset = targetTopRelativeToContainer + containerScrollTop;

      const centerOffset = container.clientHeight / 2 - ref.current.offsetHeight / 2;

      container.scrollTo({
        top: targetOffset - centerOffset - offset,
        behavior: 'smooth',
      });
    }
  };

  const currentReportId = useSelector(state => state.report.currentReportId);
  const reportId = useSelector(state => state.report.reportId);
  const chatId = useSelector(state => state.report.chatId);
  useEffect(() => {
    if (!currentReportId) return;

    const selector = `li[data-menu-id*="${currentReportId}"]`;
    let observer;
    let timeoutId;

    const tryScroll = () => {
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        dispatch(setCurrentReportId(null));
        cleanup();
      }
    };

    const cleanup = () => {
      if (observer) observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };

    // 尝试立即滚动一次
    tryScroll();

    // 设置 DOM 监听
    observer = new MutationObserver(() => {
      tryScroll();
    });

    const menuContainer = menuInnerRef.current || document;
    observer.observe(menuContainer, {
      childList: true,
      subtree: true,
    });

    // 设置超时 3 秒后清理 observer
    timeoutId = setTimeout(() => {
      console.warn('Timeout: target element not found for currentReportId:', currentReportId);
      cleanup();
      dispatch(setCurrentReportId(null)); // 清除状态避免残留
    }, 3000);

    return cleanup;
  }, [currentReportId]);
  useEffect(() => {
    if (reportId) {
      const targetElement = document.querySelector(`li[data-menu-id*="${reportId}"]`);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      dispatch(setReportId(null));
    }
  }, [reportId]);
  useEffect(() => {
    if (chatId) {
      const targetElement = document.querySelector(`li[data-menu-id*="${chatId}"]`);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      dispatch(setChatId(null));
    }
  }, [chatId]);
  const [isMenuOverflow, setIsMenuOverflow] = useState(false);

  const checkMenuOverflow = () => {
    if (menuItems.length > 0) {
      if (menuScrollRef.current && menuInnerRef.current) {
        const container = menuScrollRef.current;
        const menu = menuInnerRef.current;
        if (menu.scrollHeight > container.clientHeight) {
          setIsMenuOverflow(true);
        } else {
          setIsMenuOverflow(false);
        }
      }
    } else {
      setIsMenuOverflow(false);
    }
  };
  useEffect(() => {
    checkMenuOverflow();
  }, [menuItems, isMobile, collapsed, isMobileSiderOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setCollapsed(false);
    setIsMobileSiderOpen(false);
  }, [isMobile]);

  useEffect(() => {
    dispatch(checkLoginStatus());
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedIn) {
      getInitialData();
    }
  }, [isLoggedIn]);
  const menuScrollRef = useRef(null);
  const menuInnerRef = useRef(null);

  const isMenuDataReady = useMemo(() => {
    return conversionList.length > 0 || reportList.length > 0;
  }, [conversionList, reportList]);

  useEffect(() => {
    if (isMenuDataReady) {
      const defaultMenuItems = [
        {
          key: '/Conversation',
          icon: <MessageOutlined />,
          children: renderConversionItem(),
          type: collapsed ? '' : 'group',
          label: <div ref={conversationDividerRef} style={{ position: 'relative' }}></div>,
        },
        {
          key: '/Report',
          icon: <ProfileOutlined />,
          label: (
            <div ref={reportDividerRef} style={{ position: 'relative' }}>
              <Divider
                orientation="center"
                plain
                className="no-line-divider"
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#999',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: themeMode === 'dark' ? '#2F2F2F' : '#f5f5f5',
                  height: '50px',
                  margin: 0,
                  cursor: 'pointer',
                }}
              >
                {t('report')}
              </Divider>
            </div>
          ),
          children: renderReportItem(),
          type: collapsed ? '' : 'group',
        },
      ];

      if (!isMobile && collapsed) {
        setMenuItems([
          {
            key: '/Expand',
            icon: <RightSquareOutlined />,
            label: 'Expand',
            onClick: () => {
              setCollapsed(false);
              setShowAll(false);
            },
          },
          {
            key: '/Expand',
            icon: (
              <PlusOutlined
                style={{
                  color: '#f5f5f5',
                  backgroundColor: '#16796f',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  justifyContent: 'center',
                  margin: '0 -10px',
                }}
              />
            ),
            label: 'Chat',
            onClick: () => {
              navigate('/');
            },
          },
          ...defaultMenuItems,
        ]);
      } else {
        setMenuItems(defaultMenuItems);
      }
    } else {
      setMenuItems([]);
    }
  }, [
    collapsed,
    isMenuDataReady,
    renameId,
    activePopoverId,
    hiddenMessageId,
    t,
    isMobile,
    themeMode,
    conversionList,
    reportList,
    showAll,
  ]);

  useEffect(() => {
    setDefaultMenuKeys();
    if (isMobile) {
      setIsMobileSiderOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isMobile) {
      setHeaderTitle();
    } else {
      dispatch(setCurrentTitle('Scams.Report'));
    }
  }, [location.pathname, conversionList, reportList]);

  const setHeaderTitle = () => {
    const pathname = location.pathname;
    if (/\/chat\/*/.test(pathname)) {
      const target = conversionList.find(item => item.externalRef === currentExternalRef.current);
      dispatch(setCurrentTitle(target?.name));
    } else if (/\/report\/*/.test(pathname)) {
      const target = reportList.find(item => item.externalRef === currentExternalRef.current);
      dispatch(setCurrentTitle(target?.reportName));
    } else {
      dispatch(setCurrentTitle('Scams.Report'));
    }
  };

  const setDefaultMenuKeys = () => {
    const pathname = location.pathname;
    if (/\/chat\/*/.test(pathname)) {
      !isMobile && setMenuOpenKeys(['/Conversation']);
      setMenuSelectedKeys([location.pathname]);
    } else if (/\/report\/*/.test(pathname)) {
      !isMobile && setMenuOpenKeys(['/Report']);
      setMenuSelectedKeys([location.pathname]);
    } else {
      setMenuSelectedKeys([]);
      setMenuOpenKeys([]);
    }
  };

  const getInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([dispatch(getConversionListThunk()), dispatch(getRepostLitThunk())]);
    } catch (error) {
      console.error('Error during data fetching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuEllipsisClick = e => {
    e.stopPropagation();
  };

  const popoverCommonConfig = {
    overlayInnerStyle: { padding: '.1rem' },
    trigger: 'click',
    placement: 'bottomLeft',
    arrow: false,
  };
  //格式化时间
  const parseDate = dateStr => {
    return new Date(dateStr);
  };

  // 根据 updatedAt 字段分组会话数据
  const groupConversationsByUpdatedAt = conversations => {
    const currentDate = new Date();
    const todayStart = new Date(currentDate);
    todayStart.setHours(0, 0, 0, 0);

    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(todayStart.getDate() - 1);

    const sevenDaysAgo = new Date(todayStart);
    sevenDaysAgo.setDate(todayStart.getDate() - 7);

    const thirtyDaysAgo = new Date(todayStart);
    thirtyDaysAgo.setDate(todayStart.getDate() - 30);

    // 暂存数据
    const today = [];
    const yesterday = [];
    const last7Days = [];
    const last30Days = [];
    const monthGroups = {};

    conversations.forEach(conv => {
      const updatedAt = parseDate(conv.updatedAt);

      if (updatedAt >= todayStart) {
        today.push(conv);
      } else if (updatedAt >= yesterdayStart) {
        yesterday.push(conv);
      } else if (updatedAt >= sevenDaysAgo) {
        last7Days.push(conv);
      } else if (updatedAt >= thirtyDaysAgo) {
        last30Days.push(conv);
      } else {
        const monthYear = updatedAt.toISOString().split('-').slice(0, 2).join('-'); // "YYYY-MM"
        if (!monthGroups[monthYear]) {
          monthGroups[monthYear] = [];
        }
        monthGroups[monthYear].push(conv);
      }
    });
    // 构建有序 Map
    const groups = new Map();
    if (isLoggedIn) {
      if (today.length) groups.set(t('Today'), today);
      if (yesterday.length) groups.set(t('Yesterday'), yesterday);
      if (last7Days.length) groups.set(t('Last7Days'), last7Days);
      if (last30Days.length) groups.set(t('Last30Days'), last30Days);
    }

    // 直接使用Object.keys()原始顺序，不排序
    Object.keys(monthGroups).forEach(key => {
      groups.set(key, monthGroups[key]);
    });

    return groups;
  };

  const effectiveShowAll = isMobile ? true : showAll;

  const renderConversionItem = () => {
    const groups = groupConversationsByUpdatedAt(conversionList); // Map 类型

    const items = [];

    for (const [groupTitle, conversations] of groups) {
      // 每个组加一个分组时间标题项
      items.push({
        key: `group-${groupTitle}`,
        type: 'group',
        label: (
          <Col
            style={{
              textAlign: 'left',
              fontSize: 12,
              fontWeight: 500,
              color: '#999',
              margin: '1px 0',
            }}
          >
            {groupTitle}
          </Col>
        ),
      });

      // 遍历分组下的每一个会话 item
      conversations.forEach(item => {
        items.push({
          key: `/chat/${item.externalRef}`,
          label: (
            <Row gutter={[10, 10]} style={{ width: '20rem' }}>
              <Tooltip
                title={isMobile ? null : <span style={{ whiteSpace: 'nowrap' }}>{item.name}</span>}
                overlayStyle={{ maxWidth: '30vw' }}
              >
                <Col flex="1" style={{ overflow: 'hidden', marginRight: '.5rem' }}>
                  {renameId === item.externalRef ? (
                    <RenameInput
                      menuName={item.name}
                      externalRef={item.externalRef}
                      onSave={(id, val) => {
                        updateConversationNameAPI({ externalRef: id, name: val });
                        dispatch(updateConversionAttr({ conversionId: id, data: { name: val } }));
                        setRenameId(null);
                      }}
                    />
                  ) : (
                    <>
                      {item.reportId ? (
                        <span style={{ color: '#FF6633' }}>{item.name}</span>
                      ) : (
                        item.name
                      )}
                    </>
                  )}
                </Col>
              </Tooltip>
              <ConversionMenuItem item={item} />
            </Row>
          ),
        });
      });
    }

    if (showAll || effectiveShowAll) return items;
    return items.slice(0, 20);
  };

  const ConversionMenuItem = ({ item }) => {
    return (
      <Col flex="24px">
        {(item.isWhitelistFlag == 1 || item.auditStatus == '1') &&
          userProfile.userStatus === 'Login User' &&
          !hiddenMessageId[item.externalRef] && <MessageOutlined style={{ color: '#16796f' }} />}
        <Popover
          content={editConversation(item.externalRef, item.reportId, item.name)}
          open={item.externalRef === activePopoverId}
          onOpenChange={visible => {
            setActivePopoverId(visible ? item.externalRef : null);
          }}
          {...popoverCommonConfig}
        >
          <EllipsisOutlined onClick={handleMenuEllipsisClick} />
        </Popover>
      </Col>
    );
  };

  const renderReportItem = () => {
    const items = reportList.map(item => ({
      key: `/report/${item.externalRef}`,
      label: (
        <Row gutter={[10, 10]} style={{ width: '20rem' }}>
          <Tooltip
            title={
              isMobile ? null : <span style={{ whiteSpace: 'nowrap' }}>{item.reportName}</span>
            }
            overlayStyle={{ maxWidth: '30vw' }}
          >
            <Col flex="1" style={{ overflow: 'hidden', marginRight: '.5rem' }}>
              {renameId === item.externalRef ? (
                <RenameInput
                  menuName={item.reportName}
                  externalRef={item.externalRef}
                  onSave={(id, val) => {
                    updateNameAPI({ externalRef: id, reportName: val });
                    dispatch(updateReportAttr({ reportId: id, data: { reportName: val } }));
                    setRenameId(null);
                  }}
                />
              ) : (
                <span>{item.reportName}</span>
              )}
            </Col>
          </Tooltip>

          <Col flex="24px">
            <Popover
              content={editReport(item.externalRef, item.reportName)}
              open={item.externalRef === activePopoverId}
              onOpenChange={visible => {
                setActivePopoverId(visible ? item.externalRef : null);
              }}
              {...popoverCommonConfig}
            >
              <EllipsisOutlined onClick={handleMenuEllipsisClick} />
            </Popover>
          </Col>
        </Row>
      ),
    }));

    if (showAll) return items;
    if (effectiveShowAll) return items;
    const convCount = renderConversionItem().length;
    if (convCount < 20) {
      return items.slice(0, 20 - convCount);
    }
    return [];
  };

  useEffect(() => {
    const handleScroll = () => {
      const container = menuScrollRef.current;
      if (!container) return;
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - 20) {
        if (!showAll && !loadingMore) {
          setLoadingMore(true);
          setTimeout(() => {
            setShowAll(true);
            setLoadingMore(false);
          }, 500);
        }
      }
    };
    const container = menuScrollRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const deleteConversation = id => {
    if (currentExternalRef.current && id === currentExternalRef.current) {
      navigate('/');
    }
    dispatch(deleteConversion(id));
    deleteConversationApi(id);
  };

  const getConversionItems = hasReport => {
    return [
      {
        key: 'conversion_edit',
        label: t('home:rename'),
        icon: <EditOutlined />,
      },
      {
        key: 'conversion_delete',
        label: t('translation:delete'),
        icon: <DeleteOutlined />,
        danger: true,
        disabled: hasReport,
      },
    ];
  };

  const deleteConfirm = (title, callback) => {
    confirm({
      title: t('home:deleteChat.title'),
      icon: <ExclamationCircleFilled />,
      content: (
        <span>
          {t('home:deleteChat.description')} <b>{title}</b>
        </span>
      ),
      onOk() {
        callback();
      },
      okButtonProps: {
        danger: true,
      },
      onCancel() {},
      cancelText: t('translation:cancel'),
      okText: t('home:deleteChat.button'),
    });
  };

  const handleMenuClick = (key, id, e, name = '') => {
    e.stopPropagation();
    setActivePopoverId(null);
    switch (key) {
      case 'conversion_edit':
        setRenameId(id);
        break;
      case 'conversion_delete':
        deleteConfirm(name, () => {
          deleteConversation(id);
        });
        break;
      case 'report_edit':
        setRenameId(id);
        break;
      default:
        break;
    }
  };

  const editConversation = (id, hasReport, name) => (
    <div>
      <Menu
        items={getConversionItems(hasReport)}
        selectable={false}
        onClick={({ key, domEvent }) => handleMenuClick(key, id, domEvent, name)}
      />
    </div>
  );

  const getReportItems = () => {
    return [
      {
        key: 'report_edit',
        label: 'Rename',
        icon: <EditOutlined />,
      },
    ];
  };

  const editReport = id => (
    <div>
      <Menu
        items={getReportItems()}
        selectable={false}
        onClick={({ key, domEvent }) => handleMenuClick(key, id, domEvent)}
      />
    </div>
  );

  const handleLoginModalOpen = () => {
    setIsLoginModalOpen(true);
  };

  const handleLoginModalClose = () => {
    setIsLoginModalOpen(false);
  };

  const handleLogin = provider => {
    const fromIframe = new URLSearchParams(window.location.search).get('from_iframe');
    let oauthUrl = `${config.API_BASE_URL}/oauth2/authorization/${provider}`;

    if (fromIframe === 'cyberoo') {
      oauthUrl += `?state=from_iframe`;
    }

    window.location.href = oauthUrl;
    setIsLoginModalOpen(false);
  };

  const handleSignout = () => {
    logout().then(() => {
      navigate('/');
      window.location.reload();
    });
  };

  const onMenuClick = route => {
    const parts = route.key.split('/');
    setHiddenMessageId(prev => ({ ...prev, [parts[parts.length - 1]]: true }));

    if (route.key !== '/Expand') {
      navigate(route.key);
      setMenuSelectedKeys([route.key]);
    }
    if (isMobile) {
      setIsMobileSiderOpen(false);
    }
  };

  const onOpenChange = openKeys => {
    setMenuOpenKeys(openKeys);
  };

  const renderUserMenu = useCallback(() => {
    const DefaultMenu = ({ children }) => (
      <Menu style={{ width: '200px', textAlign: 'center' }}>
        <Menu.Item key="0" icon={<CustomAvatar />}>
          &nbsp;&nbsp;&nbsp;
          {userProfile && userProfile.name}
        </Menu.Item>
        <Menu.Divider />
        {children}
        <Menu.Item
          key="1"
          icon={<LogoutOutlined style={{ fontSize: '1.7em' }} />}
          onClick={handleSignout}
        >
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{t('translation:signout')}
        </Menu.Item>
      </Menu>
    );
    if (collapsed)
      return (
        <DefaultMenu>
          <Menu.Item
            key="2"
            icon={<SettingOutlined style={{ fontSize: '1.7em' }} />}
            onClick={() => setIsSettingModalOpen(true)}
          >
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Setting
          </Menu.Item>
          <Menu.Divider />
        </DefaultMenu>
      );
    return <DefaultMenu />;
  }, [collapsed, userProfile]);

  const handleUpdateUser = async changeToDark => {
    const appearance = changeToDark ? 'dark' : 'light';
    dispatch(setThemeMode(appearance));
    updateThemeApi({ appearance });
  };

  const renderMenu = () => {
    return (
      <>
        <Row
          style={{
            width: '100%',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <Col
            style={{ width: '100%', backgroundColor: themeMode === 'dark' ? '#2F2F2F' : '#f5f5f5' }}
          >
            {!collapsed && (
              <Button
                data-testid="scams-report-new-chat-button"
                type="primary"
                iconPosition="end"
                icon={<PlusOutlined />}
                style={{
                  width: collapsed ? '50%' : '100%',
                  margin: '0 auto',
                  borderRadius: '1rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                onClick={() => navigate('/')}
              >
                {!collapsed && t('home:new.chat')}
              </Button>
            )}
            {/* 顶部 Divider */}
            {!collapsed && isLoggedIn && (
              <Divider
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#999',
                  position: 'sticky',
                  top: 34,
                  zIndex: 10,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '50px',
                  marginTop: '2px',
                  marginBottom: 0,
                  cursor: 'pointer',
                }}
                onClick={() => scrollToRef(conversationDividerRef, 100)}
              >
                {t('conversation')}
              </Divider>
            )}
          </Col>
        </Row>
        {/* 顶部 Divider */}
        {!collapsed && isReportLeaving && isMenuOverflow && (
          <Divider
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: '#999',
              position: 'sticky',
              top: 80,
              zIndex: 10,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: themeMode === 'dark' ? '#2F2F2F' : '#f5f5f5',
              height: '50px',
            }}
          >
            {t('report')}
          </Divider>
        )}
        <div ref={menuInnerRef} style={{ width: '100%' }}>
          <Menu
            selectedKeys={menuSelectedKeys}
            openKeys={menuOpenKeys}
            mode="inline"
            onClick={onMenuClick}
            onOpenChange={onOpenChange}
            items={menuItems}
            style={{ height: 'auto' }}
          />
        </div>

        {/* 低部 Divider */}
        {!collapsed && !isReport && isMenuOverflow && !isReportLeaving && (
          <Divider
            style={{
              fontSize: 14,
              fontWeight: 500,
              height: '50px',
              color: '#999',
              position: 'sticky',
              bottom: 0,
              zIndex: 10,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: themeMode === 'dark' ? '#2F2F2F' : '#f5f5f5',
              cursor: 'pointer',
            }}
            onClick={() => scrollToRef(reportDividerRef)}
          >
            {t('report')}
          </Divider>
        )}
      </>
    );
  };

  const renderSiderHeader = () => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: '20px 10px',
        }}
      >
        <Image
          width={50}
          src={avatarDay}
          preview={false}
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer', margin: '6px' }}
          draggable={false}
        />
        {!collapsed && (
          <Space size="large">
            <label
              className="intro-text"
              style={{ marginLeft: '25px', fontWeight: 'bold', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <Text style={{ fontSize: '18px' }}>Scams Report</Text>
            </label>
            <Text>
              <LeftSquareOutlined
                className="sider-trigger"
                style={{ marginLeft: 'auto', cursor: 'pointer', float: 'right' }}
                onClick={() => {
                  setCollapsed(true);
                  setShowAll(false);
                }}
              />
            </Text>
          </Space>
        )}
      </div>
    );
  };

  const renderSiderFooter = () => {
    return (
      <div className="login-footer">
        {!userProfile && (
          <UserOutlined
            type="primary"
            block
            onClick={handleLoginModalOpen}
            style={{
              fontSize: '1.7em',
              color: themeMode === 'dark' ? '#FFFFFF' : 'defaultColor',
            }}
          >
            Login
          </UserOutlined>
        )}
        {userProfile && (
          <div className="user-actions">
            <Dropdown overlay={renderUserMenu()}>
              <Button
                icon={<CustomAvatar />}
                style={{
                  border: 'none',
                  boxShadow: 'none',
                  backgroundColor: 'inherit',
                  margin: collapsed ? '0 auto' : '',
                }}
              >
                {!collapsed && <span className="userName-style">{userProfile.name}</span>}
              </Button>
            </Dropdown>

            {!collapsed && (
              <div>
                {themeMode === 'dark' ? (
                  <Text>
                    <SunOutlined className="icon-style" onClick={() => handleUpdateUser(false)} />
                  </Text>
                ) : (
                  <Text>
                    <MoonOutlined className="icon-style" onClick={() => handleUpdateUser(true)} />
                  </Text>
                )}
                <Text>
                  <SettingOutlined
                    className="icon-style"
                    onClick={() => setIsSettingModalOpen(true)}
                  />
                </Text>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSider = () => {
    return (
      <Sider
        theme={themeMode}
        collapsible
        width={260}
        collapsed={collapsed}
        trigger={null}
        style={{
          height: '100vh',
          position: 'relative',
        }}
      >
        <div style={{ position: 'relative', height: '100%' }}>
          <div style={{ position: 'absolute', top: 0, zIndex: 2 }}>{renderSiderHeader()}</div>
          <div
            ref={menuScrollRef}
            onScroll={onMenuScroll}
            style={{
              position: 'absolute',
              top: '90px',
              left: 0,
              right: 0,
              bottom: '70px',
              overflow: 'auto',
            }}
          >
            {renderMenu()}

            <div style={{ textAlign: 'center', padding: '10px' }}>{loadingMore && <Spin />}</div>
          </div>
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2 }}>
            {renderSiderFooter()}
          </div>
        </div>
      </Sider>
    );
  };

  const renderHeaderRight = () => {
    if (isLoggedIn) {
      return (
        <Link to="/">
          <Button type="link" ghost icon={<PlusSquareOutlined />}></Button>
        </Link>
      );
    }
    return (
      <Button
        type="primary"
        size="small"
        style={{ borderRadius: '1rem' }}
        onClick={handleLoginModalOpen}
      >
        Login
      </Button>
    );
  };

  const renderMenuLayout = () => {
    if (isMobile) {
      return (
        <Header
          style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: themeMode === 'dark' ? '' : '#fff',
            padding: '0 .3rem',
            position: 'sticky',
            top: 0,
          }}
        >
          <Row style={{ width: '100%' }} justify="space-between" align="middle">
            <Col span={4}>
              <Button
                type="link"
                ghost
                icon={<AlignLeftOutlined />}
                onClick={() => setIsMobileSiderOpen(true)}
              ></Button>
            </Col>
            <Col
              span={10}
              style={{
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <Typography.Text strong>{currentTitle}</Typography.Text>
            </Col>
            <Col span={5} style={{ textAlign: 'end' }}>
              {isMobile && (
                <>
                  <QuestionCenter isMobile={isMobile} renderAsButton />
                </>
              )}
              {renderHeaderRight()}
            </Col>
          </Row>
        </Header>
      );
    }
    return renderSider();
  };

  return (
    <Spin spinning={loading} tip="Loading...">
      <Layout>
        {renderMenuLayout()}

        <Layout>
          <Content
            style={{
              height: isMobile ? 'calc(100svh - 48px)' : '100vh',
              display: 'flex',
              flexDirection: 'column',
              margin: '0 auto',
              overflowY: 'auto',
              overflowX: 'hidden',
              width: '100%',
            }}
          >
            <Outlet
              context={{
                isMobile,
              }}
            />
          </Content>
        </Layout>

        <Drawer
          placement="left"
          width="60%"
          closable={false}
          open={isMobileSiderOpen}
          onClose={() => setIsMobileSiderOpen(false)}
          styles={{ body: { padding: 0 } }}
        >
          {/* 抽屉顶部关闭按钮 */}
          <Row justify="end">
            <Button
              type="link"
              ghost
              icon={<AlignRightOutlined />}
              onClick={() => setIsMobileSiderOpen(false)}
            />
          </Row>

          <div
            ref={menuScrollRef}
            onScroll={onMenuScroll}
            style={{
              height: 'calc(100vh - 100px)',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {renderMenu()}
          </div>

          {/* 底部用户信息等菜单 */}
          <div>{renderSiderFooter()}</div>
        </Drawer>

        <LoginModal
          isLoginModalOpen={isLoginModalOpen}
          handleLoginModalClose={handleLoginModalClose}
          handleLogin={handleLogin}
        />

        {userProfile && (
          <Setting isModalOpen={isSettingModalOpen} setIsModalOpen={setIsSettingModalOpen} />
        )}
        <div
          style={{
            position: 'fixed',
            bottom: '5px',
            left: isMobile ? '50%' : collapsed ? 'calc(50% + 10px)' : 'calc(50% + 130px)',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            fontSize: '13px',
            width: 'max-content',
            transition: 'left 0.3s ease',
            color: 'gray',
            maxWidth: isMobile ? '90%' : 'none',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            zIndex: 999,
          }}
        >
          {t('home:adviceNotice')}
        </div>

        {!isMobile && <QuestionCenter isMobile={isMobile} />}
      </Layout>
    </Spin>
  );
};

export default HomePage;
